import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import {
  useAddCampaignStep,
  useAffinityListConfigs,
  useArchiveCampaign,
  useCampaign,
  useCampaignAudit,
  useCampaignErrors,
  useCampaignRecipients,
  useCampaignReplyForensics,
  useCampaignStats,
  useCampaignTicks,
  useCreateStepTemplate,
  useDeleteCampaign,
  useDeleteCampaignStep,
  useDuplicateCampaign,
  useEnrollCampaign,
  useFinishCampaign,
  useForms,
  useOutreachSenders,
  usePauseCampaign,
  useListCustomFields,
  useReactivateCampaign,
  useRefreshListOptions,
  useRetryRecipient,
  useSetCampaignEvent,
  useStartCampaign,
  useFinishedRecipientImpact,
  useStepDeletionImpact,
  useStepTemplates,
  useUnarchiveCampaign,
  useUpdateCampaign,
  useUpdateCampaignStep,
} from '../../hooks/useOutreach';
import {
  CAMPAIGN_AUDIT_LABELS,
} from '../../types/outreach';
import type {
  AffinityListConfig,
  Campaign,
  CampaignStep,
  CampaignStepIn,
  ReactivateCampaignRequest,
  StepTemplate,
} from '../../types/outreach';
import { formatEt, formatEtShort } from '../../utils/formatTimestamp';
import { utcIsoToWallClockInTz, wallClockInTzToUtcIso } from '../../utils/timezone';
import { useViewParam } from '../../hooks/navigation/useUrlState';
import styles from '../pages/OutreachPage.module.css';
import { CampaignForm, type CampaignFormSubmit } from './CampaignForm';
import { isNativeList, listSourceLabel, resolveFilter } from './campaignFilters';
import { CampaignRecipientsTable } from './CampaignRecipientsTable';
import { EmailBodyPreview, RichEmailEditor } from './RichEmailEditor';
import { EventTestSendModal } from './EventTestSendModal';
import { ReactivateConfirm } from './ReactivateConfirm';
import { RsvpApprovalQueue } from './RsvpApprovalQueue';
import { RefreshButton } from './RefreshButton';
import { parseSchedule } from './ScheduleFields';
import { StartCampaignConfirm } from './StartCampaignConfirm';
import { TestSendModal } from './TestSendModal';


type SubTab = 'overview' | 'steps' | 'recipients' | 'rsvp-approvals' | 'status' | 'errors' | 'activity';


// Pull a human-readable message out of an ApiError thrown by apiFetch.
// FastAPI wraps HTTPException payloads in {detail: <string|object>}; we
// surface the string form, or the object's `.message` if present, before
// falling back to the generic Error.message ("API error: 409 ...").
function formatApiErrorMessage(e: unknown): string {
  const apiErr = e as { status?: number; detail?: unknown; message?: string };
  const rawDetail = apiErr?.detail;
  const inner = (rawDetail && typeof rawDetail === 'object' && 'detail' in rawDetail)
    ? (rawDetail as { detail: unknown }).detail
    : rawDetail;
  if (typeof inner === 'string' && inner.trim().length > 0) {
    return inner;
  }
  if (inner && typeof inner === 'object') {
    const msg = (inner as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim().length > 0) return msg;
  }
  if (e instanceof Error) return e.message;
  return 'unknown error';
}


export function CampaignDetail({
  campaignId,
  onClose,
  initialSubTab,
  autoOpenAddStep,
}: {
  campaignId: number;
  onClose: () => void;
  // When set, used as the starting sub-tab instead of the
  // status-based default. CampaignsAdmin passes 'steps' after
  // creating a fresh draft so the user lands on the step editor
  // rather than the overview.
  initialSubTab?: SubTab;
  // When true, the Steps tab opens with a blank "Add step" editor
  // pre-rendered. Used by the post-create redirect so the operator's
  // next click is "type subject + body" rather than "click + new step".
  autoOpenAddStep?: boolean;
}) {
  const cQ = useCampaign(campaignId);
  // Sub-tab lives in the URL (?ctab=). It is nested INSIDE the campaign
  // selection (?campaign=), so it must use `replace`, NOT push: a push here
  // would write a null-state entry that clobbers useSelectionParam('campaign')'s
  // {emberSel:'campaign'} marker (breaking "Back to list" and leaking ctab across
  // campaigns — review F2). Replace keeps the marker and pins ctab to the
  // campaign's own history entry, so closing the campaign drops both together.
  // Empty = "no explicit choice yet" → fall back to initialSubTab (post-create
  // 'steps') then the status-based default (drafts→Overview, else→Status).
  const [ctab, setCtab] = useViewParam('ctab', '', { history: 'replace' });
  const subTab = (ctab || initialSubTab || null) as SubTab | null;
  const setSubTab = (t: SubTab) => setCtab(t);

  if (cQ.isLoading) {
    return <div className={styles.empty}>Loading campaign…</div>;
  }
  if (!cQ.data) {
    return <div className={styles.empty}>Campaign not found.</div>;
  }
  const c = cQ.data;
  const resolvedSubTab: SubTab =
    subTab ?? (c.status === 'draft' ? 'overview' : 'status');

  return (
    <div className={styles.section}>
      {/* Single header row: Back + name + TEST chip on the left,
          lifecycle actions on the right. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 4,
        }}
      >
        <button type="button" className={styles.button} onClick={onClose}>
          ← Back
        </button>
        <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
          {c.name}
        </h2>
        {/* Campaign ID — small muted, click-to-copy. Useful for
            curl-ing diagnostic endpoints (reply-forensics, /ticks,
            etc.) since the detail view isn't deep-linked yet. */}
        <span
          style={{
            fontSize: 11, fontFamily: "'DM Mono', monospace",
            color: '#888', background: '#F0F0EC',
            padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
          }}
          title="Click to copy campaign ID"
          onClick={() => {
            void navigator.clipboard?.writeText(String(c.id));
          }}
        >
          id={c.id}
        </span>
        {c.test_mode_enabled && (
          <span
            style={{
              fontSize: 11, fontFamily: "'DM Mono', monospace",
              color: '#B04030', background: '#FCEFEC',
              padding: '2px 8px', borderRadius: 10,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
            title="This campaign is in test mode -- sends route to the test recipient"
          >
            TEST
          </span>
        )}
        <span style={{ flex: 1 }} />
        <CampaignActions campaign={c} onClose={onClose} />
      </div>

      {/* Per-campaign test-mode banner. ORs with the global Settings
          banner; either signal alone is enough to engage test-mode
          routing in the sequencer. The TEST chip in the header is
          glanceable but easy to miss — this stripe is unambiguous. */}
      {c.test_mode_enabled && <PerCampaignTestModeBanner />}

      <nav className={styles.subTabs} style={{ marginTop: 8 }}>
        {(['overview', 'steps', 'recipients', 'rsvp-approvals', 'status', 'errors', 'activity'] as SubTab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`${styles.subTab} ${resolvedSubTab === t ? styles.subTabActive : ''}`}
            onClick={() => setSubTab(t)}
          >
            {t === 'rsvp-approvals' ? 'RSVP Queue' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>

      {resolvedSubTab === 'overview' && <Overview campaign={c} />}
      {resolvedSubTab === 'steps' && (
        <Steps campaign={c} autoOpenAdd={autoOpenAddStep} />
      )}
      {resolvedSubTab === 'recipients' && (
        <CampaignRecipientsTable campaignId={c.id} campaign={c} />
      )}
      {resolvedSubTab === 'rsvp-approvals' && (
        <RsvpApprovalQueue campaignId={c.id} campaign={c} />
      )}
      {resolvedSubTab === 'status' && <Status campaignId={c.id} />}
      {resolvedSubTab === 'errors' && <ErrorsPanel campaignId={c.id} />}
      {resolvedSubTab === 'activity' && <ActivityTab campaignId={c.id} />}
    </div>
  );
}


function PerCampaignTestModeBanner() {
  return (
    <div
      style={{
        background: '#FCEFEC',
        border: '1px solid #E5B5B0',
        color: '#7B2820',
        padding: '8px 12px',
        borderRadius: 6,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        marginTop: 12,
        marginBottom: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ fontSize: 16 }}>⚠</span>
      <span>
        <strong>Test mode is ON for this campaign.</strong> Sends route
        to the global test recipient (set under Settings); no status
        write-back, no click tracking, no recipient state change. Toggle
        off via Edit on the Overview tab before going live.
      </span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Action buttons -- start, pause, finish, enroll, delete
// ─────────────────────────────────────────────────────────────────────────

function CampaignActions({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const startMut = useStartCampaign();
  const pauseMut = usePauseCampaign();
  const finishMut = useFinishCampaign();
  const enrollMut = useEnrollCampaign();
  const deleteMut = useDeleteCampaign();
  const duplicateMut = useDuplicateCampaign();
  const reactivateMut = useReactivateCampaign();
  const archiveMut = useArchiveCampaign();
  const unarchiveMut = useUnarchiveCampaign();
  const [confirmingStart, setConfirmingStart] = useState(false);
  const [confirmingReactivate, setConfirmingReactivate] = useState(false);

  async function run(action: () => Promise<unknown>, label: string) {
    try {
      const result = await action();
      // Lifecycle actions return a CampaignActionResult with a human-readable
      // `message` field. Surface it so the operator sees enrolled/excluded
      // counts (or whatever the action did) instead of a silent success.
      if (
        result && typeof result === 'object' &&
        'message' in result && typeof result.message === 'string'
      ) {
        alert(`${label}: ${result.message}`);
      }
    } catch (e: unknown) {
      alert(`${label} failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  // Color-coded lifecycle buttons. Start/Resume = green (go), Pause =
  // red (stop), Finish = black (terminal/sober). Enroll + Delete keep
  // their default neutral / danger styling.
  const greenBtn: React.CSSProperties = {
    background: '#2E6E2E', color: '#FFF', borderColor: '#2E6E2E',
  };
  const redBtn: React.CSSProperties = {
    background: '#B04030', color: '#FFF', borderColor: '#B04030',
  };
  const blackBtn: React.CSSProperties = {
    background: '#222', color: '#FFF', borderColor: '#222',
  };

  async function handleReactivate(body: ReactivateCampaignRequest) {
    setConfirmingReactivate(false);
    await run(
      () => reactivateMut.mutateAsync({ campaignId: campaign.id, body }),
      'Reactivate',
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {/* Enroll is available whenever the sequencer is NOT actively
          sending — draft (pre-launch), paused (between bursts), or
          finished (re-arming for a follow-on cycle). The endpoint is
          idempotent: it just appends list members not already
          enrolled, so re-running is safe and lets operators pick up
          new Affinity invitees without going through the full
          Reactivate flow. Hidden during 'active' so we don't quietly
          inject new pending rows mid-flight. */}
      {campaign.status !== 'active' && (
        <button
          type="button"
          className={styles.button}
          onClick={() => run(() => enrollMut.mutateAsync(campaign.id), 'Enroll')}
          disabled={enrollMut.isPending}
          title="Pull new list members from the list source into this campaign. Idempotent — already-enrolled recipients are skipped."
        >
          {enrollMut.isPending ? 'Enrolling…' : 'Enroll recipients'}
        </button>
      )}
      {/* Draft → Active: classic Start path with the live/test confirm
          modal. Resume from paused is handled below by Reactivate, not
          Start, so the operator can choose enroll-on-resume + which
          paused recipient buckets to revive. */}
      {campaign.status === 'draft' && (
        <button
          type="button"
          className={styles.button}
          style={greenBtn}
          onClick={() => setConfirmingStart(true)}
          disabled={startMut.isPending}
        >
          Start sending
        </button>
      )}
      {confirmingStart && (
        <StartCampaignConfirm
          campaign={campaign}
          onCancel={() => setConfirmingStart(false)}
          onConfirm={async () => {
            setConfirmingStart(false);
            // Try without force first. On 409 (data_issues_unresolved),
            // surface the structured summary and let the admin
            // explicitly opt into force=true (recipients with broken
            // data will land in needs_data and not be sent until
            // Refresh merge fields fixes them).
            try {
              const r = await startMut.mutateAsync({
                campaignId: campaign.id, force: false,
              });
              if (r?.message) alert(`Start: ${r.message}`);
            } catch (e: unknown) {
              const apiErr = e as { status?: number; detail?: unknown };
              // FastAPI wraps HTTPException payloads inside {detail: ...}.
              // Unwrap once so both the structured data-issue case and
              // plain-string raises (e.g. "campaign has no form linked")
              // surface their actual content.
              const rawDetail = apiErr?.detail;
              const inner = (rawDetail && typeof rawDetail === 'object' && 'detail' in rawDetail)
                ? (rawDetail as { detail: unknown }).detail
                : rawDetail;
              const dataIssue =
                apiErr?.status === 409 &&
                inner !== null &&
                typeof inner === 'object' &&
                (inner as { error?: string }).error === 'data_issues_unresolved'
                  ? (inner as {
                      message: string;
                      data_issues: {
                        recipients_with_blank_required: number;
                        org_groups_with_divergence: number;
                        sample_variable_names: string[];
                      };
                    })
                  : null;
              if (dataIssue) {
                const proceed = window.confirm(
                  `${dataIssue.message}\n\n` +
                  `Recipients with blank required fields: ` +
                  `${dataIssue.data_issues.recipients_with_blank_required}\n` +
                  `Orgs with divergent values: ` +
                  `${dataIssue.data_issues.org_groups_with_divergence}\n` +
                  `Affected merge fields: ` +
                  `${dataIssue.data_issues.sample_variable_names.join(', ') || '(none)'}\n\n` +
                  `Click OK to start anyway (blocked recipients will go to ` +
                  `needs_data); Cancel to fix the source data first then ` +
                  `Refresh merge fields.`,
                );
                if (proceed) {
                  try {
                    const r2 = await startMut.mutateAsync({
                      campaignId: campaign.id, force: true,
                    });
                    if (r2?.message) alert(`Start: ${r2.message}`);
                  } catch (e2) {
                    const innerMsg = formatApiErrorMessage(e2);
                    alert(`Start failed: ${innerMsg}`);
                  }
                }
              } else {
                alert(`Start failed: ${formatApiErrorMessage(e)}`);
              }
            }
          }}
        />
      )}
      {/* Reactivate replaces Resume for paused + adds a path for
          finished. Single modal asks the three DQ-7 questions. */}
      {(campaign.status === 'paused' || campaign.status === 'finished') && (
        <button
          type="button"
          className={styles.button}
          style={greenBtn}
          onClick={() => setConfirmingReactivate(true)}
          disabled={reactivateMut.isPending}
        >
          {reactivateMut.isPending ? 'Reactivating…' : 'Reactivate'}
        </button>
      )}
      {confirmingReactivate && (
        <ReactivateConfirm
          campaign={campaign}
          onCancel={() => setConfirmingReactivate(false)}
          onConfirm={handleReactivate}
          isPending={reactivateMut.isPending}
        />
      )}
      {campaign.status === 'active' && (
        <button
          type="button"
          className={styles.button}
          style={redBtn}
          onClick={() => run(() => pauseMut.mutateAsync(campaign.id), 'Pause')}
          disabled={pauseMut.isPending}
        >
          Pause
        </button>
      )}
      {campaign.status !== 'finished' && (
        <button
          type="button"
          className={styles.button}
          style={blackBtn}
          onClick={() => {
            if (window.confirm('Finish this campaign? In-flight recipients will stop.')) {
              run(() => finishMut.mutateAsync(campaign.id), 'Finish');
            }
          }}
          disabled={finishMut.isPending}
        >
          Finish
        </button>
      )}
      {/* Duplicate is available regardless of status — operators
          frequently clone a finished campaign for a "round 2" cycle.
          The clone lands as a fresh draft with no recipients enrolled. */}
      <button
        type="button"
        className={styles.button}
        onClick={async () => {
          try {
            const clone = await duplicateMut.mutateAsync(campaign.id);
            // Navigate to the new draft so the operator can edit + enroll.
            // onClose(newId) is the existing pattern used by CampaignCreate.
            onClose();
            // Defer routing to the parent — passing the id back through
            // onClose would require widening the signature; instead the
            // parent's campaign list refetch (invalidated in the hook)
            // surfaces the clone as soon as the user lands back on it.
            const hadEvent = campaign.event_starts_at != null;
            alert(
              `Duplicated as draft #${clone.id} ("${clone.name}"). ` +
                `Open it from the Campaigns list to edit + enroll.` +
                (hadEvent
                  ? '\n\nNote: Event details were copied, but Send ' +
                    'calendar invites is OFF on the duplicate. Turn it ' +
                    'on from the Overview tab when the clone is ready ' +
                    'to fire calendar invites.'
                  : ''),
            );
          } catch (e: unknown) {
            alert(
              `Duplicate failed: ${e instanceof Error ? e.message : 'unknown error'}`,
            );
          }
        }}
        disabled={duplicateMut.isPending}
        title="Clone this campaign as a fresh draft (steps copied; no recipients enrolled)."
      >
        {duplicateMut.isPending ? 'Duplicating…' : 'Duplicate'}
      </button>

      {/* Archive / Unarchive — surfaced alongside Duplicate so all
          state-management actions cluster on the right of the row.
          Archive is blocked while active (sequencer is sending);
          unarchive is the symmetric inverse. Hidden on a draft you
          haven't done anything with -- the row-level Archive in the
          campaigns list handles those. */}
      {campaign.status !== 'active' && !campaign.archived_at && (
        <button
          type="button"
          className={styles.button}
          onClick={async () => {
            await run(() => archiveMut.mutateAsync(campaign.id), 'Archive');
            onClose();
          }}
          disabled={archiveMut.isPending}
          title="Hide from the active campaigns list. Reversible — sequencer + status write-back behavior unchanged."
        >
          {archiveMut.isPending ? 'Archiving…' : 'Archive'}
        </button>
      )}
      {campaign.archived_at && (
        <button
          type="button"
          className={styles.button}
          onClick={() =>
            run(() => unarchiveMut.mutateAsync(campaign.id), 'Unarchive')
          }
          disabled={unarchiveMut.isPending}
          title="Restore to the active campaigns list."
        >
          {unarchiveMut.isPending ? 'Restoring…' : 'Unarchive'}
        </button>
      )}

      {campaign.status === 'draft' && (
        <button
          type="button"
          className={`${styles.button} ${styles.buttonDanger}`}
          onClick={async () => {
            if (
              window.confirm(
                'Delete this draft campaign? This is irreversible.',
              )
            ) {
              await run(() => deleteMut.mutateAsync(campaign.id), 'Delete');
              onClose();
            }
          }}
          disabled={deleteMut.isPending}
        >
          Delete
        </button>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Sub-views
// ─────────────────────────────────────────────────────────────────────────

function Overview({ campaign }: { campaign: Campaign }) {
  const [editing, setEditing] = useState(false);
  const listsQ = useAffinityListConfigs();
  const formsQ = useForms();
  if (editing) {
    return (
      <OverviewEdit campaign={campaign} onClose={() => setEditing(false)} />
    );
  }

  const list = listsQ.data?.find(
    (l) => l.affinity_list_id === campaign.affinity_list_id,
  );
  // Show "AGM 2026 (346659)" — name first for scannability, id in
  // parens because it's still useful for cross-checking against
  // Affinity / log lines.
  const listLabel = list
    ? `${list.label || list.list_slug} (${campaign.affinity_list_id})`
    : String(campaign.affinity_list_id);

  const linkedForm = (formsQ.data || []).find(
    (f) => f.id === campaign.form_id,
  );

  const filter = resolveFilter(campaign.affinity_filter_json, list);
  const rsvpOnlyFilter = resolveFilter(campaign.rsvp_only_filter_json, list);
  const sched = parseSchedule(campaign.schedule_json);
  const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: 'flex', justifyContent: 'flex-end', marginBottom: 8,
        }}
      >
        <button
          type="button"
          className={styles.button}
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
      </div>
      <table className={styles.table}>
        <tbody>
          <Row label="Name" value={campaign.name} />
          <Row label="Status" value={campaign.status} />
          <Row label="Sender" value={campaign.sender_email} mono />
          <Row
            label={isNativeList(list) ? 'List' : 'Affinity list'}
            value={listLabel}
          />
          <Row
            label="Form"
            value={
              linkedForm ? (
                <span>
                  {linkedForm.name}{' '}
                  <span className={styles.helpText}>
                    (/forms/{linkedForm.slug})
                  </span>
                </span>
              ) : (
                <span style={{ color: '#B04030' }}>
                  not linked · required before starting
                </span>
              )
            }
          />
          <Row
            label="Filter by Status"
            value={
              filter.status ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {filter.status.values.length === 0 ? (
                    <span className={styles.helpText}>—</span>
                  ) : (
                    filter.status.values.map((v) => (
                      <ReadonlyChip key={v} label={v} />
                    ))
                  )}
                </div>
              ) : filter.rawJson ? (
                // Legacy shape (`{"Status": "<label>"}`) — show the raw
                // JSON so the operator can see what's stored. The sync
                // path rewrites this to the structured shape on first
                // edit, so it self-heals over time.
                <span className={styles.code}>{filter.rawJson}</span>
              ) : (
                <span className={styles.helpText}>—</span>
              )
            }
          />
          {filter.scope && (
            <Row
              label={`Filter by ${filter.scope.fieldName || 'Scope'}`}
              value={
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {filter.scope.values.length === 0 ? (
                    <span className={styles.helpText}>—</span>
                  ) : (
                    filter.scope.values.map((v) => (
                      <ReadonlyChip key={v} label={v} />
                    ))
                  )}
                </div>
              }
            />
          )}
          {rsvpOnlyFilter.scope ? (
            <Row
              label={`RSVP-only ${rsvpOnlyFilter.scope.fieldName || 'Scope'}`}
              value={
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {rsvpOnlyFilter.scope.values.length === 0 ? (
                    <span className={styles.helpText}>—</span>
                  ) : (
                    rsvpOnlyFilter.scope.values.map((v) => (
                      <ReadonlyChip key={v} label={v} />
                    ))
                  )}
                </div>
              }
            />
          ) : rsvpOnlyFilter.rawJson ? (
            <Row
              label="RSVP-only filter"
              value={<span className={styles.code}>{rsvpOnlyFilter.rawJson}</span>}
            />
          ) : null}
          <Row label="Send mode" value={
            campaign.send_mode === 'per_person'
              ? 'Per person (one email per recipient)'
              : campaign.send_mode === 'per_organization'
              ? 'Per organization (one email per org)'
              : campaign.send_mode
          } />
          <Row
            label="Throttle (sec/sender)"
            value={String(campaign.min_seconds_between_sends_per_inbox)}
          />
          <Row
            label="Click tracking"
            value={campaign.track_clicks ? 'enabled' : 'disabled'}
          />
          <Row
            label="Open tracking"
            value={campaign.track_opens ? 'enabled (estimated image loads)' : 'disabled'}
          />
          <Row
            label="Test mode"
            value={campaign.test_mode_enabled ? 'ON (sends to test recipient)' : 'off'}
          />
          <Row
            label="Send schedule"
            value={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {weekdayLabels.map((lbl, i) => {
                    const iso = i + 1;
                    const on = sched.weekdays.includes(iso);
                    return on ? (
                      <ReadonlyChip key={lbl} label={`✓ ${lbl}`} />
                    ) : (
                      <span
                        key={lbl}
                        style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          background: '#FAF9F4',
                          color: '#999',
                          borderRadius: 4,
                          fontSize: 12,
                          fontFamily: "'DM Sans', sans-serif",
                          textDecoration: 'line-through',
                        }}
                      >
                        {lbl}
                      </span>
                    );
                  })}
                </div>
                <div className={styles.helpText}>
                  Sends fire from <strong>{_hourLabel(sched.start_hour)}</strong>
                  {' '}up to (but not including){' '}
                  <strong>{_hourLabel(sched.end_hour)}</strong>
                  {' · '}{sched.timezone}.
                </div>
              </div>
            }
          />
          <Row label="CC override" value={campaign.cc_override || '—'} mono />
          <Row label="BCC override" value={campaign.bcc_override || '—'} mono />
          <Row
            label="Direct replies to"
            value={campaign.reply_to_email || '— (sender)'}
            mono
          />
          <Row label="Created" value={formatEt(campaign.created_at)} />
          <Row label="Steps" value={String(campaign.steps.length)} />
        </tbody>
      </table>

      <EventDetailsCard campaign={campaign} />
    </div>
  );
}


function OverviewEdit({
  campaign,
  onClose,
}: {
  campaign: Campaign;
  onClose: () => void;
}) {
  const updateMut = useUpdateCampaign();
  const sendersQ = useOutreachSenders();
  const listsQ = useAffinityListConfigs();
  const refreshMut = useRefreshListOptions();

  // Auto-refresh the Status + Scope dropdown caches against Affinity
  // when the cached values are stale (>10 min) or have never been
  // refreshed. Fires once per Edit-open per campaign. New Affinity
  // options surface in the filter dropdowns without re-seeding.
  const list = listsQ.data?.find(
    (l) => l.affinity_list_id === campaign.affinity_list_id,
  );
  const triggeredRef = useRef(false);
  useEffect(() => {
    if (triggeredRef.current) return;
    if (!list) return;
    const refreshedAt = list.dropdown_options_refreshed_at
      ? new Date(list.dropdown_options_refreshed_at).getTime()
      : 0;
    const stale = Date.now() - refreshedAt > 10 * 60 * 1000;
    if (stale) {
      triggeredRef.current = true;
      refreshMut.mutate(list.affinity_list_id);
    }
  }, [list, refreshMut]);

  async function handleSubmit(body: CampaignFormSubmit) {
    if (body._mode !== 'edit') return; // type narrowing safety
    const { _mode, ...rest } = body;
    await updateMut.mutateAsync({ campaignId: campaign.id, body: rest });
    onClose();
  }

  return (
    <div>
      <RefreshOptionsBanner
        list={list}
        isRefreshing={refreshMut.isPending}
        error={refreshMut.isError ? refreshMut.error : null}
        onRefresh={() => {
          if (!list) return;
          triggeredRef.current = true;
          refreshMut.mutate(list.affinity_list_id);
        }}
      />
      <CampaignForm
        mode="edit"
        initial={campaign}
        listConfigs={listsQ.data || []}
        senders={sendersQ.data || []}
        onSubmit={handleSubmit}
        onCancel={onClose}
        submitLabel={updateMut.isPending ? 'Saving…' : 'Save'}
        isSubmitting={updateMut.isPending}
        title="Edit campaign settings"
      />
    </div>
  );
}


function RefreshOptionsBanner({
  list,
  isRefreshing,
  error,
  onRefresh,
}: {
  list: AffinityListConfig | undefined;
  isRefreshing: boolean;
  error: Error | null;
  onRefresh: () => void;
}) {
  if (!list) return null;
  // "Affinity" on affinity-sourced lists, "the CRM list" once flipped.
  const sourceLabel = listSourceLabel(list);
  let statusLabel: string;
  if (isRefreshing) {
    statusLabel = `Refreshing options from ${sourceLabel}…`;
  } else if (error) {
    statusLabel = `Refresh failed: ${error.message}`;
  } else if (list.dropdown_options_refreshed_at) {
    statusLabel = `Status + Campaign options refreshed ${formatEtShort(
      list.dropdown_options_refreshed_at,
    )}`;
  } else {
    statusLabel =
      `Status + Campaign options have never been refreshed from ${sourceLabel}`;
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 12px',
        marginBottom: 12,
        background: error ? '#FEF2EE' : '#FAF9F4',
        border: error ? '1px solid #B04030' : '1px solid #E5E2D9',
        borderRadius: 4,
        fontSize: 12,
        color: error ? '#B04030' : '#666',
      }}
    >
      <span>{statusLabel}</span>
      <button
        type="button"
        className={styles.button}
        onClick={onRefresh}
        disabled={isRefreshing}
        style={{ fontSize: 12, padding: '4px 10px' }}
      >
        {isRefreshing ? 'Refreshing…' : `Refresh from ${sourceLabel}`}
      </button>
    </div>
  );
}


function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <tr>
      <td style={{ width: 200, color: '#888', fontSize: 12 }}>{label}</td>
      <td className={mono ? styles.code : ''}>{value}</td>
    </tr>
  );
}


// Compact pill used in the static Overview to render resolved
// filter values + weekday selections. Visually similar to the toggle
// buttons in the edit form so the read-only view echoes the editor's
// shape. Read-only by design (no onClick).
function ReadonlyChip({ label }: { label: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        background: '#222',
        color: '#FFF',
        borderRadius: 4,
        fontSize: 12,
        fontFamily: "'DM Sans', sans-serif",
        lineHeight: 1.4,
      }}
    >
      {label}
    </span>
  );
}


function _hourLabel(h: number): string {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h === 24) return '12 AM (next)';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}


function Steps({
  campaign,
  autoOpenAdd,
}: {
  campaign: Campaign;
  autoOpenAdd?: boolean;
}) {
  // Step edits are now allowed at any campaign status -- changes apply
  // on a go-forward basis. Banner explains the semantic for non-drafts.
  const isLive = campaign.status !== 'draft';
  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  // autoOpenAdd: post-create redirect lands on Steps with a blank
  // editor pre-rendered. Initialize directly rather than via effect
  // so the editor is visible on first paint without a flash.
  const [adding, setAdding] = useState(
    Boolean(autoOpenAdd && campaign.steps.length === 0),
  );
  const [deletingStep, setDeletingStep] = useState<CampaignStep | null>(null);
  const [testSendStep, setTestSendStep] = useState<CampaignStep | null>(null);

  // Recipients backing the per-step test-send picker. The modal needs at
  // least one row; if none exist we disable the button on each StepRow.
  const recipientsQ = useCampaignRecipients(campaign.id);
  const eligibleRecipients = useMemo(
    () =>
      (recipientsQ.data?.rows || []).filter(
        (r) => r.status !== 'removed' && r.enrollment_kind === 'campaign',
      ),
    [recipientsQ.data?.rows],
  );

  return (
    <div style={{ marginTop: 16 }}>
      {isLive && (
        <div
          style={{
            background: '#FFF7E0', border: '1px solid #F0D070',
            color: '#7B5400', padding: '8px 12px', borderRadius: 6,
            fontSize: 12, marginBottom: 12,
          }}
        >
          ⚠ This campaign is <strong>{campaign.status}</strong>. Step edits
          apply going forward — recipients who already received a step keep
          their original copy. <em>Delay changes re-anchor in-queue
          recipients: next_send_at is recomputed as (last successful send
          on the prior step) + new delay.</em>
        </div>
      )}

      {campaign.steps.length === 0 ? (
        <div className={styles.empty}>No steps configured.</div>
      ) : (
        campaign.steps.map((s) =>
          editingStepId === s.id ? (
            <StepEditor
              key={s.id}
              campaignId={campaign.id}
              campaign={campaign}
              step={s}
              onClose={() => setEditingStepId(null)}
            />
          ) : (
            <StepRow
              key={s.id}
              step={s}
              onEdit={() => setEditingStepId(s.id)}
              onRequestDelete={() => setDeletingStep(s)}
              onRequestTest={() => setTestSendStep(s)}
              testEnabled={eligibleRecipients.length > 0}
            />
          ),
        )
      )}

      {!adding && (
        <button
          type="button"
          className={styles.button}
          onClick={() => setAdding(true)}
        >
          + Add step
        </button>
      )}
      {adding && (
        <StepEditor
          campaignId={campaign.id}
          campaign={campaign}
          step={null}
          defaultStepOrder={
            (campaign.steps.length
              ? Math.max(...campaign.steps.map((s) => s.step_order))
              : 0) + 1
          }
          onClose={() => setAdding(false)}
        />
      )}

      {deletingStep && (
        <DeleteStepConfirm
          campaignId={campaign.id}
          step={deletingStep}
          onClose={() => setDeletingStep(null)}
        />
      )}

      {testSendStep && eligibleRecipients.length > 0 && (
        <TestSendModal
          campaignId={campaign.id}
          recipient={eligibleRecipients[0]}
          recipientOptions={eligibleRecipients}
          initialStepOrder={testSendStep.step_order}
          onClose={() => setTestSendStep(null)}
        />
      )}
    </div>
  );
}


function formatStepDelay(step: CampaignStep): string {
  // Resolve the effective delay in minutes: prefer delay_minutes when
  // non-zero, fall back to legacy delay_days * 1440. Mirrors the
  // backend sequencer's resolution logic.
  const minutes =
    (step.delay_minutes ?? 0) > 0
      ? (step.delay_minutes ?? 0)
      : (step.delay_days ?? 0) * 1440;
  if (minutes === 0) return 'sends immediately';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);
  return `wait ${parts.join(' ')}`;
}


function StepRow({
  step, onEdit, onRequestDelete, onRequestTest, testEnabled,
}: {
  step: CampaignStep;
  onEdit: () => void;
  onRequestDelete: () => void;
  onRequestTest: () => void;
  testEnabled: boolean;
}) {
  return (
    <div className={styles.section} style={{ background: '#FAF9F4' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>Step {step.step_order}</strong>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={styles.helpText}>
            {formatStepDelay(step)}
            {' · '}
            {step.send_condition === 'if_no_reply' ? 'stops on reply' : 'always sends'}
          </span>
          <button
            type="button"
            className={styles.button}
            onClick={onRequestTest}
            disabled={!testEnabled}
            title={
              testEnabled
                ? `Render this step against an enrolled recipient and send to an internal address.`
                : 'Enroll at least one recipient first.'
            }
          >
            Send test
          </button>
          <button type="button" className={styles.button} onClick={onEdit}>
            Edit
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonDanger}`}
            onClick={onRequestDelete}
          >
            Delete
          </button>
        </div>
      </div>
      <div className={styles.label}>Subject</div>
      <div className={styles.code}>{step.subject_template}</div>
      <div className={styles.label} style={{ marginTop: 8 }}>Body preview</div>
      {/* Same CSS the editor canvas uses, so saved-state preview and
          edit-state canvas render identically. */}
      <EmailBodyPreview html={step.body_html_template} />
    </div>
  );
}


function DeleteStepConfirm({
  campaignId, step, onClose,
}: {
  campaignId: number;
  step: CampaignStep;
  onClose: () => void;
}) {
  const impactQ = useStepDeletionImpact(campaignId, step.id, true);
  const deleteMut = useDeleteCampaignStep();

  async function confirmDelete() {
    try {
      await deleteMut.mutateAsync({ campaignId, stepId: step.id });
      onClose();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed.');
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', padding: 20, borderRadius: 8,
          minWidth: 480, maxWidth: 600,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>
          Delete step {step.step_order}?
        </h3>
        {impactQ.isLoading ? (
          <div className={styles.helpText}>Computing impact…</div>
        ) : impactQ.data ? (
          <div style={{ fontSize: 13, marginBottom: 16 }}>
            <p style={{ margin: '0 0 8px' }}>
              <strong>{impactQ.data.in_flight_recipients_skipping}</strong>{' '}
              in-flight recipient{impactQ.data.in_flight_recipients_skipping === 1 ? '' : 's'} will
              skip past this step to the next remaining one.
            </p>
            {impactQ.data.total_sends_for_step > 0 && (
              <p style={{ margin: '0 0 8px' }}>
                <strong>{impactQ.data.total_sends_for_step}</strong> historical
                send{impactQ.data.total_sends_for_step === 1 ? '' : 's'} for
                this step are already in recipient inboxes — those are unaffected.
              </p>
            )}
            {impactQ.data.already_passed > 0 && (
              <p style={{ margin: '0 0 8px' }}>
                <strong>{impactQ.data.already_passed}</strong>{' '}
                recipient{impactQ.data.already_passed === 1 ? '' : 's'} already
                passed this step — no impact.
              </p>
            )}
            <p
              style={{
                fontSize: 11, color: '#888', margin: '8px 0 0',
              }}
            >
              The step's HTML template will be deleted. This is reversible
              only by re-adding the step manually.
            </p>
          </div>
        ) : (
          <div className={styles.error}>Failed to load impact.</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonDanger}`}
            onClick={confirmDelete}
            disabled={deleteMut.isPending}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete step'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Renders the earliest/latest next_send_at forecast for the
// reactivation flow plus warnings (campaign-not-active +
// imminent-send). Pure presentational; reads everything from the
// preview endpoint's response.
function ReactivateImpactDetail({
  impact,
}: {
  impact: import('../../types/outreach').FinishedRecipientImpact;
}) {
  // "Imminent" check anchors `now` to the panel's mount via useState's
  // initializer (runs once, doesn't violate the render-purity rule the
  // way Date.now() in the render body would). The 2-min window is
  // generous enough that the few seconds of drift between mount and
  // operator clicking save don't matter.
  const [openedAt] = useState(() => Date.now());
  // Backend serializes naive UTC datetimes as ISO without a `Z`
  // suffix. Plain `new Date(iso)` would interpret that as local
  // time and silently misrepresent the timestamp by the browser's
  // UTC offset. Force-append `Z` (unless an explicit offset is
  // already present) so the conversion to local TZ works
  // correctly. Same trick applied to the imminent-send check.
  const asUtc = (iso: string): string =>
    /Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z';
  const fmt = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(asUtc(iso)).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };
  const imminent = impact.earliest_next_send_at != null
    && new Date(asUtc(impact.earliest_next_send_at)).getTime() - openedAt
      < 2 * 60 * 1000;
  return (
    <div
      style={{
        marginTop: 8,
        marginLeft: 24,
        fontSize: 12,
        color: '#555',
        lineHeight: 1.5,
      }}
    >
      <div>
        <strong>Earliest send:</strong> {fmt(impact.earliest_next_send_at)}
      </div>
      <div>
        <strong>Latest send:</strong> {fmt(impact.latest_next_send_at)}
      </div>
      {impact.skipped_no_prior_send > 0 && (
        <div style={{ marginTop: 4 }}>
          ({impact.skipped_no_prior_send} additional finished recipient
          {impact.skipped_no_prior_send === 1 ? '' : 's'} cannot be
          re-activated — no prior send on record.)
        </div>
      )}
      {imminent && (
        <div
          style={{
            marginTop: 6,
            padding: '6px 8px',
            background: '#FCE8E6',
            border: '1px solid #E0A097',
            borderRadius: 4,
          }}
        >
          ⚠ Earliest send is within ~2 minutes. The sequencer's next
          tick will fire these emails.
        </div>
      )}
      {impact.sends_blocked_by_campaign_status && (
        <div
          style={{
            marginTop: 6,
            padding: '6px 8px',
            background: '#FFF8E1',
            border: '1px solid #E0C97A',
            borderRadius: 4,
          }}
        >
          ⚠ Campaign is paused or finished — recipients will sit in
          <code> pending </code> until you reactivate the campaign.
        </div>
      )}
    </div>
  );
}


function StepEditor({
  campaignId,
  campaign,
  step,
  defaultStepOrder,
  onClose,
}: {
  campaignId: number;
  campaign: Campaign;
  step: CampaignStep | null;
  defaultStepOrder?: number;
  onClose: () => void;
}) {
  const isNew = step == null;
  const addMut = useAddCampaignStep();
  const updateMut = useUpdateCampaignStep();

  // Feature 2: pull the list's custom merge fields so the rich-editor
  // picker surfaces them alongside the built-ins. This is the
  // dynamic-by-campaign-list answer to "where do my new custom fields
  // show up?" -- the list is queried on every open, so adding/removing
  // a custom field is immediately reflected here without any
  // per-campaign refresh action.
  const customFieldsQ = useListCustomFields(campaign.affinity_list_id);
  const stepEditorCustomFields = (customFieldsQ.data || []).map((cf) => ({
    token: cf.variable_name,
    label: cf.affinity_field_name,
  }));

  const [stepOrder, setStepOrder] = useState(step?.step_order ?? defaultStepOrder ?? 1);

  // Item L: compound delay picker. The DB stores delay_minutes (new
  // canonical column) + delay_days (deprecated fallback). On load,
  // prefer delay_minutes; if zero, fall back to delay_days * 1440.
  // On save, write delay_minutes = days*1440 + hours*60 + minutes; we
  // also send delay_days = floor(total_minutes / 1440) for backend
  // backwards compat during the transition period.
  const initialMinutes = (() => {
    if (step != null) {
      const m = step.delay_minutes ?? 0;
      if (m > 0) return m;
      return (step.delay_days ?? 0) * 1440;
    }
    return isNew ? 7 * 1440 : 0;  // default: 7 days for a new step
  })();
  const [delayDaysPart, setDelayDaysPart] = useState(
    Math.floor(initialMinutes / 1440),
  );
  const [delayHoursPart, setDelayHoursPart] = useState(
    Math.floor((initialMinutes % 1440) / 60),
  );
  const [delayMinutesPart, setDelayMinutesPart] = useState(
    initialMinutes % 60,
  );
  const totalDelayMinutes =
    delayDaysPart * 1440 + delayHoursPart * 60 + delayMinutesPart;
  const [subject, setSubject] = useState(step?.subject_template ?? '');
  const [bodyHtml, setBodyHtml] = useState(step?.body_html_template ?? '');
  const [sendCondition, setSendCondition] = useState<'always' | 'if_no_reply'>(
    step?.send_condition ?? 'if_no_reply',
  );
  // New step 2+ defaults to quoting the prior message body (operators
  // almost always want the thread context on a follow-up). Step 1 has no
  // prior thread, so it stays off there. Editing an existing step keeps
  // its saved value. `defaultStepOrder` is the auto-incremented next order
  // passed by the Steps tab for the "Add step" flow.
  const [includeThread, setIncludeThread] = useState(
    step?.include_prior_thread ?? ((defaultStepOrder ?? 1) > 1),
  );
  const [startNewThread, setStartNewThread] = useState(
    step?.start_new_thread ?? false,
  );
  const [error, setError] = useState<string | null>(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  // Only meaningful when isNew + stepOrder > 1 + campaign has finished
  // recipients. Defaults off — operator must opt in deliberately
  // (re-firing to people who already finished is a meaningful blast).
  const [reactivateFinished, setReactivateFinished] = useState(false);

  // Live preview of how many finished recipients a new step at
  // (stepOrder, totalDelayMinutes) would re-activate. Only queried
  // when this is an add (not edit), the step is >1 (no finished
  // recipients exist for step 1), and the order is valid.
  const finishedImpactQ = useFinishedRecipientImpact(
    isNew && stepOrder > 1 ? campaignId : null,
    stepOrder,
    totalDelayMinutes,
    isNew && stepOrder > 1,
  );
  const impact = finishedImpactQ.data;

  // Step 1 always seeds a thread (subject required, no reply mode).
  // Step 2+ exposes the thread-mode toggle: reply on same thread
  // (default; subject hidden + auto-derives from Step 1) vs send as
  // a new email (subject required, no In-Reply-To).
  const isStep1 = stepOrder <= 1;
  const showSubjectInput = isStep1 || startNewThread;
  // When the user is in "reply on same thread" mode, the subject is
  // computed at send time (sequencer prepends "Re: " to Step 1's
  // subject); the input is hidden + the saved subject_template is
  // empty. Re-show the input if they flip back to "new thread".
  const effectiveSubject = showSubjectInput ? subject : '';

  async function save() {
    setError(null);
    if (showSubjectInput && !subject.trim()) {
      return setError('Subject is required.');
    }
    if (!bodyHtml.trim()) return setError('Body is required.');
    const body: CampaignStepIn = {
      step_order: stepOrder,
      // delay_days is deprecated but kept for backend backwards-compat
      // during the Phase 1 transition. Backend prefers delay_minutes
      // when non-zero. We fill both to keep older clients/readers
      // showing a sensible day count.
      delay_days: Math.floor(totalDelayMinutes / 1440),
      delay_minutes: totalDelayMinutes,
      subject_template: effectiveSubject,
      body_html_template: bodyHtml,
      send_condition: sendCondition,
      include_prior_thread: includeThread,
      // Step 1 ignores this server-side, but we send false explicitly
      // so the API contract is consistent.
      start_new_thread: isStep1 ? false : startNewThread,
    };
    try {
      if (isNew) {
        const result = await addMut.mutateAsync({
          campaignId,
          body: { ...body, reactivate_finished_recipients: reactivateFinished },
        });
        if (result.reactivated_count > 0) {
          const parts = [
            `Step added. Re-activated ${result.reactivated_count}`
              + ` finished recipient${result.reactivated_count === 1 ? '' : 's'}.`,
          ];
          if (result.sends_blocked_by_campaign_status) {
            parts.push(
              'Campaign is paused/finished — sends will not fire until you reactivate the campaign.',
            );
          }
          alert(parts.join(' '));
        }
      } else {
        await updateMut.mutateAsync({ campaignId, stepId: step!.id, body });
      }
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  const busy = addMut.isPending || updateMut.isPending;

  return (
    <div className={styles.section} style={{ background: '#FAF9F4' }}>
      <h3 style={{ margin: '0 0 12px', fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
        {isNew ? 'New step' : `Edit step ${step!.step_order}`}
      </h3>
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.formGrid}>
        <label className={styles.label}>Step order</label>
        <input
          className={styles.input}
          type="number"
          value={stepOrder}
          min={1}
          onChange={(e) => setStepOrder(Number(e.target.value))}
        />

        <label className={styles.label}>Delay before sending</label>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className={styles.input}
            type="number"
            value={delayDaysPart}
            min={0}
            onChange={(e) => setDelayDaysPart(Math.max(0, Number(e.target.value)))}
            style={{ width: 60 }}
          />
          <span style={{ fontSize: 13, color: '#666' }}>days</span>
          <input
            className={styles.input}
            type="number"
            value={delayHoursPart}
            min={0}
            max={23}
            onChange={(e) => setDelayHoursPart(
              Math.max(0, Math.min(23, Number(e.target.value))),
            )}
            style={{ width: 60 }}
          />
          <span style={{ fontSize: 13, color: '#666' }}>hours</span>
          <input
            className={styles.input}
            type="number"
            value={delayMinutesPart}
            min={0}
            max={59}
            onChange={(e) => setDelayMinutesPart(
              Math.max(0, Math.min(59, Number(e.target.value))),
            )}
            style={{ width: 60 }}
          />
          <span style={{ fontSize: 13, color: '#666' }}>min</span>
          <span
            className={styles.helpText}
            style={{ marginLeft: 12 }}
            title="Total minutes the engine waits after the previous step before sending this one."
          >
            {totalDelayMinutes === 0
              ? '(sends immediately)'
              : `= ${totalDelayMinutes} min`}
          </span>
        </div>

        <label className={styles.label}>Send condition</label>
        <select
          className={styles.input}
          value={sendCondition}
          onChange={(e) => setSendCondition(e.target.value as 'always' | 'if_no_reply')}
        >
          <option value="if_no_reply">Stop sending if recipient replied</option>
          <option value="always">Always send (don't stop on reply)</option>
        </select>

        {isNew && stepOrder > 1 && impact && impact.eligible > 0 && (
          <>
            <label className={styles.label}>
              Finished recipients
            </label>
            <div
              style={{
                fontSize: 13,
                padding: '10px 12px',
                background: '#FFF8E1',
                border: '1px solid #E0C97A',
                borderRadius: 6,
              }}
            >
              <label
                style={{
                  fontWeight: 'normal',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={reactivateFinished}
                  onChange={(e) => setReactivateFinished(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{impact.eligible}</strong>{' '}
                  recipient{impact.eligible === 1 ? '' : 's'} already
                  completed prior steps (status: <code>finished</code>).
                  Also send this new step to them.
                </span>
              </label>
              {reactivateFinished && (
                <ReactivateImpactDetail impact={impact} />
              )}
              {!reactivateFinished && (
                <div
                  className={styles.helpText}
                  style={{ marginTop: 6, marginLeft: 24 }}
                >
                  Leave unchecked to apply this step only to recipients
                  who haven't reached it yet.
                </div>
              )}
            </div>
          </>
        )}

        {!isStep1 && (
          <>
            <label className={styles.label}>Thread mode</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontWeight: 'normal', fontSize: 13 }}>
                <input
                  type="radio"
                  name="thread-mode"
                  checked={!startNewThread}
                  onChange={() => setStartNewThread(false)}
                />{' '}
                Reply on the same thread{' '}
                <span className={styles.helpText}>
                  (subject auto-derives from Step 1; Gmail / Outlook
                  group it as a reply)
                </span>
              </label>
              <label style={{ fontWeight: 'normal', fontSize: 13 }}>
                <input
                  type="radio"
                  name="thread-mode"
                  checked={startNewThread}
                  onChange={() => setStartNewThread(true)}
                />{' '}
                Send as a new email{' '}
                <span className={styles.helpText}>
                  (operator subject + no In-Reply-To header; starts a
                  fresh conversation)
                </span>
              </label>
            </div>

            <label className={styles.label}>
              Quote prior thread?
            </label>
            <div>
              <label style={{ fontWeight: 'normal', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={includeThread}
                  onChange={(e) => setIncludeThread(e.target.checked)}
                  disabled={startNewThread}
                />{' '}
                Include prior message body as quoted text
                {startNewThread && (
                  <span className={styles.helpText}>
                    {' '}(disabled — fresh thread)
                  </span>
                )}
              </label>
            </div>
          </>
        )}

        {showSubjectInput && (
          <>
            <label className={styles.label}>Subject</label>
            <input
              className={styles.input}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                isStep1
                  ? 'Please join us at Ember\'s 2026 AGM, {{FirstName}}'
                  : 'Re: ... or a fresh subject for the new thread'
              }
            />
          </>
        )}

      </div>

      <div style={{ marginTop: 12 }}>
        <label className={styles.label}>Body</label>
        <RichEmailEditor
          value={bodyHtml}
          onChange={setBodyHtml}
          sendMode={campaign.send_mode}
          customFields={stepEditorCustomFields}
        />
        <div className={styles.helpText} style={{ marginTop: 4 }}>
          Use the <code>{'{{ }}'}</code> menu to insert merge fields. The
          <code>HTML</code> button toggles raw source for power-user edits.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className={styles.button}
            onClick={() => setShowSaveTemplate(true)}
            disabled={!bodyHtml.trim()}
            title={
              bodyHtml.trim()
                ? 'Persist this step\'s subject + body as a named template, reusable on other campaigns.'
                : 'Add some body content first.'
            }
          >
            Save as template
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={() => setShowLoadTemplate(true)}
          >
            Load template
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Saving…' : isNew ? 'Add step' : 'Save changes'}
          </button>
        </div>
      </div>

      {showSaveTemplate && (
        <SaveTemplateModal
          subject={effectiveSubject}
          bodyHtml={bodyHtml}
          onClose={() => setShowSaveTemplate(false)}
        />
      )}
      {showLoadTemplate && (
        <LoadTemplateModal
          sendMode={campaign.send_mode}
          hasUnsavedContent={
            (effectiveSubject.trim().length > 0) || (bodyHtml.trim().length > 0)
          }
          onLoad={(t) => {
            // If the step is in reply-mode, drop the template's subject
            // (reply-mode steps store empty subject_template).
            if (showSubjectInput) {
              setSubject(t.subject_template ?? '');
            }
            setBodyHtml(t.body_html_template);
          }}
          onClose={() => setShowLoadTemplate(false)}
        />
      )}
    </div>
  );
}


function SaveTemplateModal({
  subject, bodyHtml, onClose,
}: {
  subject: string;
  bodyHtml: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMut = useCreateStepTemplate();

  async function handleSave() {
    setError(null);
    if (!name.trim()) return setError('Name is required.');
    try {
      await createMut.mutateAsync({
        name: name.trim(),
        subject_template: subject.trim() || null,
        body_html_template: bodyHtml,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', padding: 20, borderRadius: 8,
          minWidth: 460, maxWidth: 560,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>
          Save as template
        </h3>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
          Stores the current subject + body under a name. Mode compatibility
          is auto-detected from the merge fields used.
        </div>
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.formGrid}>
          <label className={styles.label}>Name</label>
          <input
            className={styles.input}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="e.g. AGM 2026 Step 1"
            autoFocus
          />
          <label className={styles.label}>Notes</label>
          <textarea
            className={styles.input}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Optional. Never rendered."
            style={{ resize: 'vertical' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleSave}
            disabled={createMut.isPending}
          >
            {createMut.isPending ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </div>
    </div>
  );
}


function LoadTemplateModal({
  sendMode, hasUnsavedContent, onLoad, onClose,
}: {
  sendMode: 'per_person' | 'per_organization';
  hasUnsavedContent: boolean;
  onLoad: (t: StepTemplate) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [pickedId, setPickedId] = useState<number | null>(null);
  // Default to the campaign's send_mode + 'both' so authors only see
  // templates that will actually render correctly. Manual override
  // lets them browse incompatible templates as a last resort (e.g. to
  // copy fragments).
  const [showAllModes, setShowAllModes] = useState(false);
  const compatFilter = showAllModes ? undefined : `${sendMode},both`;
  const templatesQ = useStepTemplates({
    archived: false,
    compat: compatFilter,
    search: search.trim() || undefined,
  });
  const picked = useMemo(
    () => templatesQ.data?.find((t) => t.id === pickedId) || null,
    [templatesQ.data, pickedId],
  );

  function handleLoad() {
    if (!picked) return;
    if (hasUnsavedContent) {
      if (!window.confirm(
        'Replace current subject + body with this template?',
      )) return;
    }
    onLoad(picked);
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', padding: 20, borderRadius: 8,
          width: 760, maxWidth: '95vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            Load template
          </h3>
          <label style={{ fontSize: 12, color: '#666' }}>
            <input
              type="checkbox"
              checked={showAllModes}
              onChange={(e) => setShowAllModes(e.target.checked)}
            />{' '}
            Show templates not compatible with {sendMode}
          </label>
        </div>
        <input
          className={styles.input}
          type="text"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginTop: 8 }}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: 12,
            marginTop: 12,
            flex: 1, minHeight: 0,
          }}
        >
          <div style={{ overflowY: 'auto', borderRight: '1px solid #EEE', paddingRight: 8 }}>
            {templatesQ.isLoading && <div className={styles.empty}>Loading…</div>}
            {templatesQ.data && templatesQ.data.length === 0 && (
              <div className={styles.empty} style={{ fontSize: 12 }}>
                No matching templates.
              </div>
            )}
            {templatesQ.data?.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setPickedId(t.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: 8, marginBottom: 4,
                  border: '1px solid',
                  borderColor: pickedId === t.id ? '#1A4F8A' : '#EEE',
                  borderRadius: 4, background: '#FFF', cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {t.send_mode_compat === 'per_person' && 'Per person'}
                  {t.send_mode_compat === 'per_organization' && 'Per org'}
                  {t.send_mode_compat === 'both' && 'Any mode'}
                  {' · '}
                  {t.variables_used.length || 0} vars
                </div>
              </button>
            ))}
          </div>

          <div style={{ overflowY: 'auto' }}>
            {picked ? (
              <div>
                <div className={styles.label}>Subject</div>
                <div className={styles.code} style={{ marginBottom: 8 }}>
                  {picked.subject_template || <em>(no subject — reply-mode)</em>}
                </div>
                <div className={styles.label}>Body</div>
                <EmailBodyPreview html={picked.body_html_template} />
                {picked.notes && (
                  <>
                    <div className={styles.label} style={{ marginTop: 8 }}>Notes</div>
                    <div className={styles.helpText}>{picked.notes}</div>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.empty} style={{ fontSize: 12 }}>
                Pick a template on the left to preview it.
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleLoad}
            disabled={!picked}
          >
            Load into editor
          </button>
        </div>
      </div>
    </div>
  );
}


function Status({ campaignId }: { campaignId: number }) {
  const [includeTest, setIncludeTest] = useState(false);
  const sQ = useCampaignStats(campaignId, includeTest);
  const tQ = useCampaignTicks(campaignId, { includeTest, limit: 50 });

  if (sQ.isLoading) return <div className={styles.empty}>Loading…</div>;
  if (!sQ.data) return <div className={styles.empty}>No data yet.</div>;
  const s = sQ.data;
  const test = s.test;
  const replyRate = s.total_sends ? (s.total_replies / s.total_sends) * 100 : 0;
  const clickRate = s.total_sends ? (s.total_clicks / s.total_sends) * 100 : 0;
  const openRate = s.total_sends ? (s.total_opens / s.total_sends) * 100 : 0;
  const bounceRate = s.total_sends ? (s.total_hard_bounces / s.total_sends) * 100 : 0;
  const liveSendsByStep = new Map(s.by_step.map((r) => [r.step_order, r]));
  const testSendsByStep = test
    ? new Map(test.by_step.map((r) => [r.step_order, r]))
    : new Map();

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12, gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: '#666' }}>
          {includeTest && test
            ? `Live + test mode (${test.total_sends} test send${test.total_sends === 1 ? '' : 's'})`
            : 'Live outreach only'}
          <span style={{ marginLeft: 8, color: '#999' }}>
            · auto-refreshes every 30s
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RefreshButton
            onClick={() => { sQ.refetch(); tQ.refetch(); }}
            isFetching={sQ.isFetching || tQ.isFetching}
            title="Re-fetch stats + recent activity now"
          />
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 13, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <input
              type="checkbox"
              checked={includeTest}
              onChange={(e) => setIncludeTest(e.target.checked)}
            />
            Show test runs
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label="Recipients" value={s.total_recipients} />
        <Tile
          label="Sends"
          value={s.total_sends}
          sub={test ? `+${test.total_sends} test` : undefined}
        />
        <Tile label="Replies" value={s.total_replies} sub={`${replyRate.toFixed(1)}%`} />
        <Tile
          label="Opens"
          value={s.total_opens}
          sub={`${openRate.toFixed(1)}% · estimated`}
        />
        <Tile
          label="Clicks"
          value={s.total_clicks}
          sub={
            test
              ? `${clickRate.toFixed(1)}% · +${test.total_clicks} test`
              : `${clickRate.toFixed(1)}%`
          }
        />
        <Tile
          label="Hard bounces" value={s.total_hard_bounces}
          sub={`${bounceRate.toFixed(1)}%`}
        />
        {s.total_unmatched_replies > 0 && (
          <Tile
            label="Unmatched replies (global)"
            value={s.total_unmatched_replies}
          />
        )}
      </div>

      <h3 style={{ fontSize: 14, fontFamily: "'DM Sans', sans-serif", margin: '20px 0 8px' }}>
        By step
      </h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Step</th>
            <th>Sent</th>
            <th>Opened</th>
            <th>Clicked</th>
            <th>Hard bounced</th>
            {test && <th>Test sends</th>}
          </tr>
        </thead>
        <tbody>
          {s.by_step.map((row) => {
            const t = testSendsByStep.get(row.step_order);
            return (
              <tr key={row.step_order}>
                <td>Step {row.step_order}</td>
                <td>{row.sent}</td>
                <td>{row.opened}</td>
                <td>{row.clicked}</td>
                <td>{row.bounced}</td>
                {test && (
                  <td>{t ? `${t.sent} (${t.clicked} click)` : '0'}</td>
                )}
              </tr>
            );
          })}
          {/* Quiet check: any step with live=0 + test>0 surfaces */}
          {test && Array.from(testSendsByStep.entries())
            .filter(([order]) => !liveSendsByStep.has(order))
            .map(([order, t]) => (
              <tr key={`test-only-${order}`} style={{ background: '#FAF9F4' }}>
                <td>Step {order}</td>
                <td>0</td>
                <td>0</td>
                <td>0</td>
                <td>0</td>
                <td>{t.sent}</td>
              </tr>
            ))}
        </tbody>
      </table>

      <ReplyForensicsPanel campaignId={campaignId} />

      <h3 style={{ fontSize: 14, fontFamily: "'DM Sans', sans-serif", margin: '24px 0 8px' }}>
        Recent activity
        <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>
          (last 50 events; idle ticks not shown)
        </span>
      </h3>
      {tQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : tQ.data && tQ.data.length === 0 ? (
        <div className={styles.empty}>
          No activity yet. The sequencer ticks every minute; this updates
          when there's something to do (send / throttle / error / out-of-window).
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 160 }}>When</th>
              <th>Event</th>
              <th>Recipient</th>
              <th>Step</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {(tQ.data || []).map((t) => (
              <tr key={t.id}>
                <td className={styles.code}>
                  {formatEtShort(t.occurred_at)}
                </td>
                <td>
                  <TickEventBadge eventType={t.event_type} />
                  {t.is_test && <TestChip />}
                </td>
                <td>{t.recipient_email || '—'}</td>
                <td>{t.step_order ?? '—'}</td>
                <td
                  className={styles.code}
                  style={{ fontSize: 11, color: '#555', maxWidth: 360 }}
                  title={t.detail || ''}
                >
                  {t.detail
                    ? t.detail.length > 80
                      ? t.detail.slice(0, 80) + '…'
                      : t.detail
                    : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function ReplyForensicsPanel({ campaignId }: { campaignId: number }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const fQ = useCampaignReplyForensics(campaignId, open);

  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      <button
        type="button"
        className={styles.button}
        onClick={() => setOpen((v) => !v)}
        style={{ fontSize: 12 }}
        title={
          'Diagnose REPLIED rows: shows inbound from/subject/headers + the ' +
          'matched outbound + whether the from address actually matches the ' +
          'recipient we sent to. Used to spot forwards / OOO / alias cases ' +
          "that the matcher attributed even though they weren't real replies."
        }
      >
        {open ? '▾ Hide reply diagnostics' : '▸ Reply diagnostics'}
        {fQ.data && open && (
          <span style={{ marginLeft: 8, color: '#888' }}>
            ({fQ.data.count} matched repl{fQ.data.count === 1 ? 'y' : 'ies'})
          </span>
        )}
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          {fQ.isLoading ? (
            <div className={styles.empty}>Loading forensics…</div>
          ) : fQ.isError ? (
            <div className={styles.error}>
              Failed to load forensics: {(fQ.error as Error)?.message ?? 'unknown'}
            </div>
          ) : !fQ.data || fQ.data.replies.length === 0 ? (
            <div className={styles.empty}>
              No matched replies on this campaign yet.
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: 140 }}>When</th>
                  <th>Recipient</th>
                  <th>Inbound from</th>
                  <th>Sent to</th>
                  <th title="True iff the inbound's From matches the email of the recipient we originally sent to. False = forward / OOO / alias / system reply.">
                    From matches?
                  </th>
                  <th>Subject</th>
                  <th>OOO?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fQ.data.replies.map((r) => {
                  const isOOO = !!(
                    r.inbound.auto_submitted
                    || r.inbound.x_autoreply
                    || (r.inbound.precedence
                      && /bulk|auto/i.test(r.inbound.precedence))
                    || (r.inbound.subject
                      && /^(automatic reply|out of office)/i.test(r.inbound.subject))
                  );
                  const matchOk = r.matched_send?.from_matches_sent_to === true;
                  return (
                    <Fragment key={r.reply_id}>
                      <tr
                        onClick={() => setExpandedId(
                          expandedId === r.reply_id ? null : r.reply_id,
                        )}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className={styles.code}>
                          {formatEtShort(r.received_at)}
                        </td>
                        <td>{r.recipient.email}</td>
                        <td className={styles.code}>{r.inbound.from_email}</td>
                        <td className={styles.code}>
                          {r.matched_send?.sent_to_email ?? '—'}
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: 11, fontFamily: "'DM Mono', monospace",
                              padding: '2px 6px', borderRadius: 4,
                              color: matchOk ? '#2E6E2E' : '#B04030',
                              background: matchOk ? '#E5F4E5' : '#FCEFEC',
                            }}
                          >
                            {matchOk ? 'YES' : 'NO'}
                          </span>
                        </td>
                        <td
                          style={{ fontSize: 12, maxWidth: 240, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={r.inbound.subject || ''}
                        >
                          {r.inbound.subject || '—'}
                        </td>
                        <td>
                          {isOOO ? (
                            <span
                              style={{
                                fontSize: 11, fontFamily: "'DM Mono', monospace",
                                color: '#7B5400', background: '#FFF7E0',
                                padding: '2px 6px', borderRadius: 4,
                              }}
                            >
                              OOO
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ color: '#888', fontSize: 11 }}>
                          {expandedId === r.reply_id ? '▾' : '▸'}
                        </td>
                      </tr>
                      {expandedId === r.reply_id && (
                        <tr>
                          <td colSpan={8} style={{ background: '#FAF9F4', fontSize: 12 }}>
                            <div style={{ padding: 12, fontFamily: "'DM Mono', monospace" }}>
                              <div><strong>Inbound IMID:</strong> {r.inbound.internet_message_id || '—'}</div>
                              <div><strong>In-Reply-To:</strong> {r.inbound.in_reply_to || '—'}</div>
                              <div><strong>Auto-Submitted:</strong> {r.inbound.auto_submitted || '—'}</div>
                              <div><strong>X-Autoreply:</strong> {r.inbound.x_autoreply || '—'}</div>
                              <div><strong>Precedence:</strong> {r.inbound.precedence || '—'}</div>
                              <div style={{ marginTop: 6 }}>
                                <strong>Body excerpt:</strong>
                                <div style={{ marginTop: 4, padding: 6, background: '#FFF', border: '1px solid #E5E5E0', borderRadius: 4, whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>
                                  {r.inbound.body_excerpt || '(no body excerpt)'}
                                </div>
                              </div>
                              {r.matched_send && (
                                <div style={{ marginTop: 6 }}>
                                  <strong>Matched outbound send:</strong> id={r.matched_send.send_id} ·
                                  step {r.matched_send.step_order} ·
                                  sent {formatEtShort(r.matched_send.sent_at)} ·
                                  campaign {r.matched_send.sent_to_campaign_id} ·
                                  imid {r.matched_send.send_imid}
                                </div>
                              )}
                              <div style={{ marginTop: 6 }}>
                                <strong>Match confidence:</strong> {r.match_confidence}
                              </div>
                              <details style={{ marginTop: 6 }}>
                                <summary style={{ cursor: 'pointer' }}>Raw headers</summary>
                                <pre style={{ marginTop: 4, fontSize: 11, background: '#FFF', border: '1px solid #E5E5E0', padding: 6, borderRadius: 4, maxHeight: 240, overflowY: 'auto' }}>
                                  {r.inbound.raw_headers
                                    ? JSON.stringify(r.inbound.raw_headers, null, 2)
                                    : '(no headers stored)'}
                                </pre>
                              </details>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


function TickEventBadge({ eventType }: { eventType: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    sent:                    { bg: '#E5F4E5', fg: '#2E6E2E' },
    throttled:               { bg: '#FFF7E0', fg: '#7B5400' },
    out_of_window:           { bg: '#F0F0EC', fg: '#666' },
    errored:                 { bg: '#FCEFEC', fg: '#B04030' },
    finished_no_more_steps:  { bg: '#E5E8F4', fg: '#2A4A8A' },
  };
  const { bg, fg } = colors[eventType] || { bg: '#F0F0EC', fg: '#666' };
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
        background: bg, color: fg, fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}
    >
      {eventType.replace(/_/g, ' ')}
    </span>
  );
}


function TestChip() {
  return (
    <span
      style={{
        display: 'inline-block', padding: '1px 6px', borderRadius: 8,
        background: '#FCEFEC', color: '#B04030', fontSize: 10,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase', letterSpacing: '0.04em',
        marginLeft: 4,
      }}
    >
      TEST
    </span>
  );
}


function ErrorsPanel({ campaignId }: { campaignId: number }) {
  const [includeTest, setIncludeTest] = useState(false);
  const eQ = useCampaignErrors(campaignId, { includeTest });
  const retryMut = useRetryRecipient();

  async function handleRetry(recipientId: number) {
    if (!recipientId) return;
    try {
      const r = await retryMut.mutateAsync({ campaignId, recipientId });
      alert(r.message);
    } catch (e: unknown) {
      alert(`Retry failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  if (eQ.isLoading) return <div className={styles.empty}>Loading…</div>;
  const rows = eQ.data || [];

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12, gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: '#666' }}>
          {rows.length} error{rows.length === 1 ? '' : 's'} · most recent first
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RefreshButton
            onClick={() => eQ.refetch()}
            isFetching={eQ.isFetching}
            title="Re-fetch errors now"
          />
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={includeTest}
              onChange={(e) => setIncludeTest(e.target.checked)}
            />
            Include test errors
          </label>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty}>
          No errors recorded
          {includeTest ? ' (including test).' : ' for live sends.'}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 160 }}>When</th>
              <th>Recipient</th>
              <th>Step</th>
              <th>Error</th>
              <th style={{ width: 120 }}>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tick_event_id}>
                <td className={styles.code}>
                  {formatEtShort(r.occurred_at)}
                </td>
                <td>
                  {r.recipient_email || '—'}
                  {r.is_test && <TestChip />}
                </td>
                <td>{r.step_order ?? '—'}</td>
                <td
                  style={{ maxWidth: 480, fontSize: 12, color: '#444' }}
                  title={r.detail}
                >
                  {r.detail.length > 200
                    ? r.detail.slice(0, 200) + '…'
                    : r.detail}
                </td>
                <td>
                  <span style={{ fontSize: 11, color: '#666' }}>
                    {r.recipient_status || '—'}
                  </span>
                </td>
                <td>
                  {r.recipient_status === 'errored' && r.recipient_id != null && (
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => handleRetry(r.recipient_id!)}
                      disabled={retryMut.isPending}
                      style={{ fontSize: 11, padding: '3px 8px' }}
                    >
                      {retryMut.isPending ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function Tile({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div
      style={{
        background: '#FAF9F4',
        border: '1px solid #E5E5E0',
        borderRadius: 8,
        padding: '12px 16px',
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {value.toLocaleString()}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#666' }}>{sub}</div>}
    </div>
  );
}


// Recipients table moved to CampaignRecipientsTable.tsx -- supports
// multiselect, sort, per-column filters, bulk soft-remove + restore +
// hard clear-all, plus per-row Send-test modal.


// ─────────────────────────────────────────────────────────────────────────
// Activity tab — campaign-level audit log surface (Phase A.1, DQ-11).
// Inline rather than its own file; ~80 LOC, no shared usage planned.
// ─────────────────────────────────────────────────────────────────────────


function ActivityTab({ campaignId }: { campaignId: number }) {
  const auditQ = useCampaignAudit(campaignId);

  if (auditQ.isLoading) {
    return <div className={styles.empty}>Loading…</div>;
  }
  if (auditQ.isError) {
    const msg =
      auditQ.error instanceof Error ? auditQ.error.message : 'unknown error';
    return (
      <div className={styles.empty} style={{ color: '#B04030' }}>
        Failed to load activity: {msg}
      </div>
    );
  }
  const data = auditQ.data;
  const refreshBar = (
    <div
      style={{
        display: 'flex', justifyContent: 'flex-end', marginBottom: 8,
      }}
    >
      <RefreshButton
        onClick={() => auditQ.refetch()}
        isFetching={auditQ.isFetching}
        title="Re-fetch activity log now"
      />
    </div>
  );
  if (!data || data.rows.length === 0) {
    return (
      <div style={{ marginTop: 16 }}>
        {refreshBar}
        <div className={styles.empty}>
          No activity yet. Mutations to this campaign (start, pause,
          recipient bulk ops, etc.) will appear here.
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 16 }}>
      {refreshBar}
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ minWidth: 150, whiteSpace: 'nowrap' }}>When</th>
            <th style={{ minWidth: 200 }}>Actor</th>
            <th style={{ minWidth: 150 }}>Action</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id}>
              <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                {formatEt(r.occurred_at)}
              </td>
              <td style={{ fontSize: 12 }}>{r.actor_email}</td>
              <td>
                <ActivityActionLabel action={r.action} />
              </td>
              <td
                style={{
                  fontSize: 12,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {r.detail ? formatActivityDetail(r.action, r.detail) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ActivityActionLabel({ action }: { action: string }) {
  // CAMPAIGN_AUDIT_LABELS is a closed map; an unknown server-side
  // action string falls back to a code-styled raw key so it's still
  // identifiable rather than missing.
  const label = (CAMPAIGN_AUDIT_LABELS as Record<string, string>)[action];
  if (label) {
    return <strong>{label}</strong>;
  }
  return <code>{action}</code>;
}


function formatActivityDetail(
  action: string,
  detail: Record<string, unknown>,
): string {
  // Per-action human-readable summarization. Falls through to JSON
  // for actions we haven't custom-formatted yet.
  if (action === 'updated' && Array.isArray(detail.fields_changed)) {
    return (detail.fields_changed as string[]).join(', ');
  }
  if (action === 'enrolled') {
    const enrolled = detail.enrolled ?? 0;
    const skipped = detail.skipped_already_enrolled ?? 0;
    return `+${enrolled} new${skipped ? `, ${skipped} already enrolled` : ''}`;
  }
  if (action === 'recipient_removed' || action === 'recipient_restored' || action === 'recipients_cleared') {
    return `${detail.count ?? 0} recipient(s)`;
  }
  if (action === 'step_added' || action === 'step_updated' || action === 'step_deleted') {
    const order = detail.step_order ?? detail.step_id ?? '';
    return `step ${order}`;
  }
  if (action === 'test_send') {
    return `→ ${detail.test_email ?? '—'} (step ${detail.step_order ?? '?'})`;
  }
  if (action === 'created' && typeof detail.name === 'string') {
    return detail.name as string;
  }
  // RSVP queue family: surface the recipient + the action set rather
  // than the raw JSON blob (which dominates the table).
  if (action === 'rsvp_queued' || action === 'rsvp_approved'
      || action === 'rsvp_dismissed' || action === 'rsvp_send_failed') {
    const rid = detail.recipient_id ?? '?';
    const trigger = detail.trigger_source as string | undefined;
    const verdict = detail.classifier_verdict as string | undefined;
    const statusEvent = detail.proposed_status_event as string | undefined;
    const invite = detail.proposed_send_invite as boolean | undefined;
    const actionParts: string[] = [];
    if (statusEvent) actionParts.push(`list status → ${statusEvent}`);
    if (invite) actionParts.push('send invite');
    if (actionParts.length === 0) actionParts.push('no-op');
    const triggerPart = trigger
      ? `${trigger}${verdict ? ` (${verdict})` : ''}`
      : '';
    const errorPart = detail.error_text
      ? ` · err: ${String(detail.error_text).slice(0, 80)}`
      : '';
    return `recipient #${rid}${triggerPart ? ` · ${triggerPart}` : ''}`
      + ` · ${actionParts.join(' + ')}${errorPart}`;
  }
  // Fallback: pretty-print the JSON as key:value pairs separated by ' · '.
  // Reads better than one long compact string when the cell wraps.
  return Object.entries(detail)
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' · ');
}


/**
 * Event Details card on the Overview tab. Captures the calendar event
 * metadata that the RSVP approval queue's send_invite action needs:
 * start/end (in UTC, with a TZ for display), location, body, optional
 * Teams meeting toggle.
 *
 * Collapsed by default when no event is configured -- shows a "Set
 * up event details" CTA. Expands inline when the operator clicks Edit.
 */
function EventDetailsCard({ campaign }: { campaign: Campaign }) {
  const [editing, setEditing] = useState(false);
  const [showTestSend, setShowTestSend] = useState(false);
  const setEventMut = useSetCampaignEvent();
  const updateCampaignMut = useUpdateCampaign();

  async function toggleCalendarInvites() {
    const next = !campaign.calendar_invites_enabled;
    if (
      next === false &&
      !window.confirm(
        'Pause calendar invites for this campaign?\n\n' +
        'The RSVP queue will keep populating from form responses, but ' +
        'Approve will refuse to send calendar invites until you turn ' +
        'this back on.',
      )
    ) {
      return;
    }
    try {
      await updateCampaignMut.mutateAsync({
        campaignId: campaign.id,
        body: { calendar_invites_enabled: next },
      });
    } catch (e: unknown) {
      window.alert(
        `Couldn't update calendar-invite toggle: ` +
        (e instanceof Error ? e.message : 'unknown'),
      );
    }
  }

  // Local edit state -- only used when `editing` is true.
  // datetime-local inputs use 'YYYY-MM-DDTHH:mm' (no timezone). The
  // operator picks a wall-clock time in the campaign's event_timezone;
  // we convert to UTC on save.
  const [startsLocal, setStartsLocal] = useState('');
  const [endsLocal, setEndsLocal] = useState('');
  const [subject, setSubject] = useState('');
  const [location, setLocation] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [onlineMeeting, setOnlineMeeting] = useState(false);
  const [tz, setTz] = useState('');
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    const tzInUse = campaign.event_timezone ?? 'America/New_York';
    // Datetimes are stored naive-UTC on the backend. The form expects
    // wall-clock in the campaign's timezone (operator types "9:30 AM"
    // and means 9:30 AM ET, not UTC). Convert UTC -> wall-clock for
    // pre-population. Without this, the form shows the UTC value and
    // the operator sees a 4-hour mismatch against the display row.
    setStartsLocal(utcIsoToWallClockInTz(campaign.event_starts_at, tzInUse));
    setEndsLocal(utcIsoToWallClockInTz(campaign.event_ends_at, tzInUse));
    setSubject(campaign.event_subject ?? '');
    setLocation(campaign.event_location ?? '');
    setBodyHtml(campaign.event_body_html ?? '');
    setOnlineMeeting(campaign.event_online_meeting);
    setTz(tzInUse);
    setError(null);
    setEditing(true);
  }

  async function save() {
    setError(null);
    if (!startsLocal || !endsLocal) {
      setError('Start + end are both required.');
      return;
    }
    if (endsLocal <= startsLocal) {
      setError('End must be after start.');
      return;
    }
    const tzInUse = tz.trim() || 'America/New_York';
    try {
      // Convert the wall-clock-in-tz the operator typed back to UTC for
      // storage. Backend stores naive UTC; display formatter converts
      // back to tz when rendering.
      const startUtc = wallClockInTzToUtcIso(startsLocal, tzInUse);
      const endUtc = wallClockInTzToUtcIso(endsLocal, tzInUse);
      await setEventMut.mutateAsync({
        campaignId: campaign.id,
        body: {
          event_starts_at: startUtc,
          event_ends_at: endUtc,
          event_subject: subject.trim() || null,
          event_location: location.trim() || null,
          event_body_html: bodyHtml.trim() || null,
          event_online_meeting: onlineMeeting,
          event_timezone: tzInUse,
        },
      });
      setEditing(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  const configured =
    campaign.event_starts_at != null && campaign.event_ends_at != null;

  return (
    <div
      className={styles.section}
      style={{ background: '#FAF9F4', marginTop: 16 }}
    >
      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 8,
        }}
      >
        <strong style={{ fontSize: 14 }}>Event Details (for calendar invites)</strong>
        {!editing && (
          <div style={{ display: 'flex', gap: 6 }}>
            {configured && (
              <button
                type="button"
                className={styles.button}
                onClick={() => setShowTestSend(true)}
                title="Send a real calendar invite from this campaign to an internal address for preview"
              >
                Send test
              </button>
            )}
            <button
              type="button"
              className={styles.button}
              onClick={startEdit}
            >
              {configured ? 'Edit' : 'Set up event details'}
            </button>
          </div>
        )}
      </div>
      {showTestSend && (
        <EventTestSendModal
          campaign={campaign}
          onClose={() => setShowTestSend(false)}
        />
      )}

      <div className={styles.helpText} style={{ marginBottom: 12 }}>
        Required before approving any RSVP queue row with{' '}
        <em>send invite</em>. Used to construct the Microsoft Graph
        calendar event sent to each approved recipient.
      </div>

      {/* Per-campaign toggle. When false, Approve refuses to send
          calendar invites even with event details fully configured.
          Default false on duplicate; admin flips to true when ready. */}
      <div
        style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12, padding: '10px 12px', marginBottom: 12,
          background: campaign.calendar_invites_enabled ? '#EAF6EA' : '#FFF8E6',
          border: campaign.calendar_invites_enabled
            ? '1px solid #2E6E2E33'
            : '1px solid #DDB02055',
          borderRadius: 4, fontSize: 13,
        }}
      >
        <div>
          <strong>
            Send calendar invites:{' '}
            <span
              style={{
                color: campaign.calendar_invites_enabled ? '#2E6E2E' : '#7B5400',
              }}
            >
              {campaign.calendar_invites_enabled ? 'ON' : 'OFF'}
            </span>
          </strong>
          <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
            {campaign.calendar_invites_enabled
              ? 'Approving an RSVP queue row with send-invite will fire a calendar event.'
              : (
                'RSVP queue rows will continue to populate from form responses, ' +
                'but Approve will refuse to send calendar invites until this is ON. ' +
                'Approve without invite (override) still works.'
              )}
          </div>
        </div>
        <button
          type="button"
          className={styles.button}
          onClick={toggleCalendarInvites}
          disabled={updateCampaignMut.isPending}
        >
          {updateCampaignMut.isPending
            ? 'Saving…'
            : campaign.calendar_invites_enabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>

      {editing ? (
        <div className={styles.formGrid}>
          {error && <div className={styles.error}>{error}</div>}

          <label className={styles.label}>Subject</label>
          <input
            className={styles.input}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={`Defaults to campaign name (${campaign.name})`}
            maxLength={500}
          />

          <label className={styles.label}>Start</label>
          <input
            className={styles.input}
            type="datetime-local"
            value={startsLocal}
            onChange={(e) => setStartsLocal(e.target.value)}
          />

          <label className={styles.label}>End</label>
          <input
            className={styles.input}
            type="datetime-local"
            value={endsLocal}
            onChange={(e) => setEndsLocal(e.target.value)}
          />

          <label className={styles.label}>Timezone (IANA)</label>
          <input
            className={styles.input}
            type="text"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            placeholder="America/New_York"
          />

          <label className={styles.label}>Location</label>
          <input
            className={styles.input}
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Office address, conference room, or virtual link"
          />

          <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
            <label className={styles.label} style={{ display: 'block', marginBottom: 4 }}>
              Body
            </label>
            <RichEmailEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              sendMode={campaign.send_mode}
              disabled={setEventMut.isPending}
            />
            <div className={styles.helpText} style={{ marginTop: 4 }}>
              Inline images and links work the same as step bodies. Note:
              merge fields are not substituted in calendar invites — the
              same body lands on every attendee's invite.
            </div>
          </div>

          <label className={styles.label}>Teams online meeting</label>
          <label style={{ fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={onlineMeeting}
              onChange={(e) => setOnlineMeeting(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            Add a Microsoft Teams meeting link to each invite
          </label>

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setEditing(false)}
              disabled={setEventMut.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={save}
              disabled={setEventMut.isPending}
            >
              {setEventMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : configured ? (
        <table className={styles.table}>
          <tbody>
            <Row
              label="Subject"
              value={
                campaign.event_subject
                  ? campaign.event_subject
                  : (
                    <span>
                      <span style={{ color: '#888' }}>(default: </span>
                      {campaign.name}
                      <span style={{ color: '#888' }}>)</span>
                    </span>
                  )
              }
            />
            <Row
              label="Start"
              value={formatEt(campaign.event_starts_at!) + (campaign.event_timezone ? ` · ${campaign.event_timezone}` : '')}
            />
            <Row
              label="End"
              value={formatEt(campaign.event_ends_at!) + (campaign.event_timezone ? ` · ${campaign.event_timezone}` : '')}
            />
            <Row label="Location" value={campaign.event_location || '—'} />
            <Row
              label="Teams meeting"
              value={campaign.event_online_meeting ? 'Yes — Teams link auto-added' : 'No'}
            />
            <Row
              label="Body"
              value={
                campaign.event_body_html
                  ? <div dangerouslySetInnerHTML={{ __html: campaign.event_body_html }} />
                  : '—'
              }
            />
          </tbody>
        </table>
      ) : (
        <div className={styles.helpText}>
          No event configured. The RSVP queue's <em>send invite</em>{' '}
          action will refuse until you set start + end times here.
        </div>
      )}
    </div>
  );
}
